import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { format } from "date-fns"
import { Send } from "lucide-react"
import { useNavigate } from "react-router"
import type {
  ConformityV2Gap,
  ConformityV2OverrideBlocker,
} from "../../../api/conformityApi"
import { dateFnsLocale } from "../../../utils/date"
import { McPill } from "../../shared/McPill"
import { gapReasonLabel } from "./frameworkPresentation"

const MC_BASE = "/material-compliance"

/** Supplier-grouped gap trail for a blocked framework (design: header row per
 *  supplier with the last-inquiry date and reply state, one line per missing
 *  component). "Request from suppliers" deep-links Supplier Studio with every
 *  supplier id in the group; Override renders only for callers who pass it —
 *  the parent gates on MC_CUSTOMER_DECLARATIONS_OVERRIDE and blockedReason. */
export function GapList({
  gaps,
  onOverride,
  overrideHidden,
  overridable = true,
  overrideBlocker = null,
}: {
  gaps: ConformityV2Gap[]
  /** undefined = show the role hint instead of the button (no permission). */
  onOverride?: () => void
  /** true = neither button nor hint (e.g. read-only states). */
  overrideHidden?: boolean
  /** false = the backend refuses the override outright; the button renders
   *  disabled with a hint instead of calling onOverride. */
  overridable?: boolean
  overrideBlocker?: ConformityV2OverrideBlocker | null
}) {
  const { translate, currentLanguage } = useTranslation()
  const navigate = useNavigate()

  const bySupplier = new Map<string, ConformityV2Gap[]>()
  for (const gap of gaps) {
    const key = gap.supplierId ?? gap.supplierName ?? "—"
    bySupplier.set(key, [...(bySupplier.get(key) ?? []), gap])
  }
  const supplierIds = [
    ...new Set(gaps.map((g) => g.supplierId).filter(Boolean)),
  ] as string[]

  return (
    <div className="flex flex-col gap-2.5">
      {[...bySupplier.values()].map((group, i) => {
        const head = group[0]
        const lastInquiry = group
          .map((g) => g.lastInquiryAt)
          .filter(Boolean)
          .sort()
          .at(-1)
        return (
          <div
            key={head.supplierId ?? head.supplierName ?? i}
            className="overflow-hidden rounded-md border border-white/[0.07]"
          >
            <div className="flex items-center gap-2.5 bg-grey1 px-2.5 py-2 text-[12px]">
              <span className="font-semibold text-white">
                {head.supplierName ?? "—"}
              </span>
              <span className="text-grey5">
                {translate("MC_CONF_V2_EDIT_GAP_COMPONENTS", {
                  n: group.length,
                })}
              </span>
              <span className="flex-1" />
              <span className="font-mono text-[11px] text-grey5">
                {lastInquiry
                  ? translate("MC_CONF_V2_EDIT_GAP_LAST_INQUIRY", {
                      date: format(new Date(lastInquiry), "dd MMM yyyy", {
                        locale: dateFnsLocale(currentLanguage),
                      }),
                    })
                  : translate("MC_CONF_V2_EDIT_GAP_NO_INQUIRY")}
              </span>
              {lastInquiry && head.inquiryStatus !== "answered" && (
                <McPill tone="danger">
                  {translate("MC_CONF_V2_EDIT_GAP_NO_REPLY")}
                </McPill>
              )}
            </div>
            {group.map((gap) => (
              <div
                key={gap.articleId}
                className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-3 border-t border-white/[0.07] px-2.5 py-2 text-[12px]"
              >
                <span className="font-mono text-white">
                  {gap.articleNumber ?? gap.articleId.slice(0, 8)}
                </span>
                <span className="truncate text-grey5">
                  {gap.articleName ?? ""}
                </span>
                <span className="text-[11.5px] text-grey5">
                  {translate("MC_CONF_V2_EDIT_GAP_PRODUCTS", {
                    n: gap.subjectCount,
                  })}
                </span>
                <span className="text-[11.5px] text-warning">
                  {gapReasonLabel(gap.reason, translate)}
                </span>
              </div>
            ))}
          </div>
        )
      })}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={supplierIds.length === 0}
            onClick={() =>
              navigate(
                `${MC_BASE}/supplier-studio/inquiry/new?supplierIds=${supplierIds.join(",")}`,
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md border border-grey4 px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:border-grey5 disabled:opacity-40"
          >
            <Send size={12} />
            {translate("MC_CONF_V2_EDIT_REQUEST_SUPPLIER")}
          </button>
          {!overrideHidden &&
            (onOverride ? (
              <button
                type="button"
                onClick={overridable ? onOverride : undefined}
                disabled={!overridable}
                aria-disabled={!overridable}
                className={
                  overridable
                    ? "rounded-md border border-grey4 px-3 py-1.5 text-[12.5px] font-medium text-grey5 transition-colors hover:border-grey5 hover:text-white"
                    : "cursor-not-allowed rounded-md border border-grey4/50 px-3 py-1.5 text-[12.5px] font-medium text-grey6 opacity-50"
                }
              >
                {translate("MC_CONF_V2_EDIT_OVERRIDE_BTN")}
              </button>
            ) : (
              <span className="self-center text-[11px] text-grey5">
                {translate("MC_CONF_V2_EDIT_OVERRIDE_ROLE")}
              </span>
            ))}
        </div>
        {!overrideHidden && onOverride && !overridable && overrideBlocker && (
          <span className="text-[11px] text-grey5">
            {translate("MC_CONF_V2_EDIT_OVERRIDE_BLOCKED_REASON")}
          </span>
        )}
      </div>
    </div>
  )
}
